import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Input, Label } from "reactstrap";
import ButtonComponent from "../../../../../components/ButtonComponent";
import { DEFAULT_IMAGE_URL } from "../../../../../constants/constans";
import { MenuItem } from "../../../../../redux/menuSlice";
import { useLazyGetDimensionsQuery } from "../../../../../redux/services/CustomTypeService";
import { getImageUrlWithFallback } from "../../../../../utility/Utility";
import { CustomizedCustom } from "../index";
import "./index.css";

interface CustomItemSetupModalProps {
  handleModalClose: () => void;
  showModal: boolean;
  selectedCustomItem?: MenuItem;
  singleMenuItemsList?: MenuItem[];
  control: any;
  customizedCustomItem?: CustomizedCustom;
  setCustomizedCustomItem: any;
  setSelectedCustomItem: any;
  onSaveCollectedItems: any;
  sendCustomObj?: any;
  setSendCustomObj?: any;
  setOrderItems?: any;
  orderItems?: any;
  selectedMenuItemList: any;
  setSelectedMenuItemList: any;
  customMenuItemList?: any;
  setCustomMenuItemList?: any;
  totalItems?: number;
  watch?: any;
  cartSummariesData?: any;
  isDeliveryOrder?: boolean;
  cartId?: string;
  deliveryDate?: Date;
}

interface CustomItemSection {
  availableCount: number;
  customItemType: string;
  displayName: string;
  isRequired: boolean;
  maxAllowedCount?: any;
  customMenuItemTypeId?: string;
}

interface extraItem {
  customItemType: string;
  displayName: string;
  price: any;
  type: string;
  quantity: number;
  menuItemId?: string;
}

const CustomItemSetupModal: React.FC<CustomItemSetupModalProps> = ({
  showModal,
  handleModalClose,
  selectedCustomItem,
  singleMenuItemsList,
  control,
  customizedCustomItem,
  setCustomizedCustomItem,
  orderItems,
  setOrderItems,
  selectedMenuItemList,
  setSelectedMenuItemList,
  customMenuItemList,
  setCustomMenuItemList,
  watch,
  cartSummariesData,
  isDeliveryOrder,
  cartId,
  deliveryDate,
}) => {
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(false);
  const [sectionDimensions, setSectionDimensions] = useState<{
    [key: string]: any;
  }>({});
  const [sectionLoading, setSectionLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [unavailabilityWarning, setUnavailabilityWarning] =
    useState<string>("");
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const DEFAULT_CUSTOM_VARIANT_KEY = "__default__";

  const createCustomVariantKey = (items: any[]) => {
    if (!items || items.length === 0) return DEFAULT_CUSTOM_VARIANT_KEY;

    const normalizedItems = items
      .map((item: any) => ({
        itemId: item.itemId,
        isAddOn: !!item.isAddOn,
        quantity: item.quantity || 1,
        unitPrice: item.unitPrice || 0,
      }))
      .sort((a: any, b: any) => {
        const idCompare = String(a.itemId || "").localeCompare(
          String(b.itemId || ""),
        );
        if (idCompare !== 0) return idCompare;
        const addOnCompare = Number(a.isAddOn) - Number(b.isAddOn);
        if (addOnCompare !== 0) return addOnCompare;
        const priceCompare = Number(a.unitPrice) - Number(b.unitPrice);
        if (priceCompare !== 0) return priceCompare;
        return Number(a.quantity) - Number(b.quantity);
      });

    return JSON.stringify(normalizedItems);
  };

  const RestID = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id,
  );

  const [triggerGetDimensions] = useLazyGetDimensionsQuery();

  // Inventory check utility function
  const checkInventoryAvailability = (
    itemId: string,
    requestedQuantity: number = 1,
  ): { available: boolean; availableCount: number; message?: string } => {
    // Skip inventory checks for scheduled orders with future delivery date
    const formValues = watch && watch();
    const isScheduledOrder = formValues?.isScheduledOrder;
    // const orderDate = formValues?.date;

    if (isScheduledOrder && deliveryDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const deliveryDay = new Date(deliveryDate);
      deliveryDay.setHours(0, 0, 0, 0);

      if (deliveryDay.getTime() !== today.getTime()) {
        return { available: true, availableCount: Infinity };
      }
    }

    if (!cartSummariesData || !cartSummariesData.carts) {
      return { available: true, availableCount: Infinity };
    }

    let totalAvailable = 0;

    if (isDeliveryOrder) {
      Object.values(cartSummariesData.carts).forEach((cart: any) => {
        if (cart.products && cart.products[itemId]) {
          const webDeliveryInventory =
            cart.products[itemId].channels?.WebDelivery?.availableItemCount ||
            0;
          totalAvailable += webDeliveryInventory;
        }
      });
    } else {
      if (cartId && cartSummariesData.carts[cartId]) {
        const cart = cartSummariesData.carts[cartId];
        if (cart.products && cart.products[itemId]) {
          const webPickupInventory =
            cart.products[itemId].channels?.WebPickup?.availableItemCount || 0;
          totalAvailable += webPickupInventory;
        }
      }
    }

    const available = totalAvailable >= requestedQuantity;
    return { available, availableCount: totalAvailable };
  };

  useEffect(() => {
    if (selectedCustomItem?.sections && RestID) {
      const fetchDimensions = async () => {
        const dimensionsMap: { [key: string]: any } = {};

        for (const section of selectedCustomItem.sections) {
          if (section.customMenuItemTypeId) {
            // mark this section as loading
            setSectionLoading((prev) => ({
              ...prev,
              [section.customItemType]: true,
            }));
            try {
              const dimensionsResponse = await triggerGetDimensions({
                restaurantId: RestID,
                customMenuItemTypeId: section.customMenuItemTypeId,
              }).unwrap();

              if (dimensionsResponse && dimensionsResponse.length > 0) {
                dimensionsMap[section.customItemType] = dimensionsResponse;
              }
            } catch (error) {
              console.error(
                `Failed to fetch dimensions for ${section.customItemType}:`,
                error,
              );
            } finally {
              // mark loading false regardless of success/failure
              setSectionLoading((prev) => ({
                ...prev,
                [section.customItemType]: false,
              }));
            }
          }
        }

        setSectionDimensions(dimensionsMap);
      };

      fetchDimensions();
    }
  }, [selectedCustomItem, RestID, triggerGetDimensions]);

  const checkRequiredSectionsSelection = () => {
    // First check if all required sections are selected
    const allRequiredSectionsSelected = selectedCustomItem?.sections.every(
      (section: CustomItemSection) => {
        if (!section.isRequired) return true;
        return customizedCustomItem?.collectedItems.some(
          (item) => item.customMenuItemSection === section.customItemType,
        );
      },
    );

    // Then check if there are any incomplete dimension selections
    const hasIncompleteDimensionSelections = selectedCustomItem?.sections.some(
      (section: CustomItemSection) => {
        const sectionType = section.customItemType;
        const dims = sectionDimensions[sectionType] || [];
        const currentSelections = selectedDimensionValues[sectionType] || {};
        const selectedCount = Object.keys(currentSelections).length;

        // If this section has dimensions and user has partial selection (0 < selected < total), it's incomplete
        return (
          dims.length > 0 && selectedCount > 0 && selectedCount < dims.length
        );
      },
    );

    // Check if dimension-based items are available on the selected day
    const formValues = watch && watch();
    const isScheduledOrder = formValues?.isScheduledOrder;
    const orderDate = formValues?.date;
    const effectiveDayIndex =
      isScheduledOrder && orderDate
        ? new Date(orderDate).getDay()
        : new Date().getDay();

    const dayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const selectedDayName = dayNames[effectiveDayIndex];

    // Check if any dimension items in collectedItems are not available on the selected day
    const unavailableItem = customizedCustomItem?.collectedItems.find(
      (item: any) => {
        if (item.itemType === "SECTION_ITEM") {
          const availableDays = item?.availableDays || [];
          // Empty array means not available any day, or not available on the selected day
          if (
            availableDays.length === 0 ||
            !availableDays.includes(effectiveDayIndex)
          ) {
            return true; // This item is not available
          }
        }
        return false;
      },
    );

    if (unavailableItem) {
      setUnavailabilityWarning(
        `This ${unavailableItem.displayName || "item"} is not available on ${selectedDayName}.`,
      );
      setIsSaveButtonEnabled(false);
    } else {
      setUnavailabilityWarning("");
      // Save button is enabled only if required sections are selected AND no incomplete dimension selections
      setIsSaveButtonEnabled(
        allRequiredSectionsSelected && !hasIncompleteDimensionSelections,
      );
    }
  };

  const [persistedCollectedItems, setPersistedCollectedItems] = useState<
    MenuItem[]
  >([]);
  // Track selected dimension value IDs per section and dimension
  const [selectedDimensionValues, setSelectedDimensionValues] = useState<{
    [sectionType: string]: { [dimensionId: string]: string };
  }>({});

  useEffect(() => {
    checkRequiredSectionsSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customizedCustomItem?.collectedItems, selectedDimensionValues]);

  // Calculate total price whenever collected items change
  useEffect(() => {
    const basePrice =
      selectedCustomItem?.calculatedPrice || selectedCustomItem?.price || 0;
    const extrasTotal = (customizedCustomItem?.collectedItems || []).reduce(
      (sum, item) => {
        if (
          item.itemType === "GROUP_EXTRA_ITEM" ||
          item.itemType === "SIGNAL_EXTRA_ITEM"
        ) {
          return sum + (item.price || 0);
        }
        return sum;
      },
      0,
    );
    setTotalPrice(basePrice + extrasTotal);
  }, [customizedCustomItem?.collectedItems, selectedCustomItem]);

  const handleDimensionValueCheckboxChange = (
    section: CustomItemSection,
    dimension: any,
    val: any,
    isChecked: boolean,
  ) => {
    const sectionType = section.customItemType;
    const dimensionId = dimension.id;

    // Update selectedDimensionValues state - radio behavior within each dimension
    setSelectedDimensionValues((prev) => {
      let updated = { ...prev };
      if (!updated[sectionType]) updated[sectionType] = {};

      if (isChecked) {
        updated[sectionType] = {
          ...updated[sectionType],
          [dimensionId]: val.id,
        };
      } else {
        const { [dimensionId]: _, ...rest } = updated[sectionType];
        updated[sectionType] = rest;
      }
      return updated;
    });

    // After state update, resolve the menu item for this section
    setTimeout(() => {
      const dims = sectionDimensions[sectionType] || [];

      // Get current selectedDimensionValues
      let currentDimensions = { ...selectedDimensionValues };
      if (!currentDimensions[sectionType]) currentDimensions[sectionType] = {};

      if (isChecked) {
        currentDimensions[sectionType] = {
          ...currentDimensions[sectionType],
          [dimensionId]: val.id,
        };
      } else {
        const { [dimensionId]: _, ...rest } = currentDimensions[sectionType];
        currentDimensions[sectionType] = rest;
      }

      const selectedDims = currentDimensions[sectionType] || {};

      if (dims.length > 0 && Object.keys(selectedDims).length === dims.length) {
        const found = (singleMenuItemsList || []).find((item: any) => {
          if (!item.isCustomMenuItem) return false;
          if (item.customMenuItemSection !== sectionType) return false;
          if (!item.dimensions) return false;
          return dims.every((dim: any) => {
            const mapping = item.dimensions[dim.id];
            if (!mapping) return false;
            if (Array.isArray(mapping)) {
              return mapping.some(
                (m: any) => m.dimensionValueId === selectedDims[dim.id],
              );
            }
            if (typeof mapping === "object" && mapping.dimensionValueId) {
              return mapping.dimensionValueId === selectedDims[dim.id];
            }
            if (typeof mapping === "string") {
              return mapping === selectedDims[dim.id];
            }
            return false;
          });
        });

        if (found && customizedCustomItem) {
          // Remove any previous SECTION_ITEM for this section ONLY
          let updatedCollectedItems =
            customizedCustomItem.collectedItems.filter(
              (i: any) =>
                !(
                  i.itemType === "SECTION_ITEM" &&
                  i.customMenuItemSection === sectionType
                ),
            );

          // Add the found item as SECTION_ITEM
          updatedCollectedItems = [
            ...updatedCollectedItems,
            {
              ...found,
              itemType: "SECTION_ITEM",
              customMenuItemSection: sectionType,
              isAddOn: false,
            } as any,
          ];

          setCustomizedCustomItem({
            ...customizedCustomItem,
            collectedItems: updatedCollectedItems,
          });
          setPersistedCollectedItems(updatedCollectedItems as MenuItem[]);
          checkRequiredSectionsSelection();
        }
      } else if (customizedCustomItem) {
        let updatedCollectedItems = customizedCustomItem.collectedItems.filter(
          (i: any) =>
            !(
              i.itemType === "SECTION_ITEM" &&
              i.customMenuItemSection === sectionType
            ),
        );
        setCustomizedCustomItem({
          ...customizedCustomItem,
          collectedItems: updatedCollectedItems,
        });
        setPersistedCollectedItems(updatedCollectedItems as MenuItem[]);
        checkRequiredSectionsSelection();
      }
    }, 0);
  };

  useEffect(() => {
    if (showModal && orderItems && orderItems.length > 0) {
      const currentItemId = selectedCustomItem?.id;

      const matchingOrderItem = orderItems.find(
        (oi: any) => oi.id === currentItemId || oi.itemId === currentItemId,
      );

      if (matchingOrderItem) {
        // Update customizedCustomItem with the latest data
        if (customizedCustomItem) {
          const restoredCollectedItems = (
            matchingOrderItem.customItems || []
          ).map((ci: any) => {
            const lookupId = ci.menuItemId || ci.itemId;
            const found = singleMenuItemsList?.find(
              (m: any) => (m.id || m.itemId) === lookupId,
            );

            if (found) {
              return {
                ...found,
                itemType: ci.isAddOn ? "GROUP_EXTRA_ITEM" : "SECTION_ITEM",
                isAddOn: ci.isAddOn || false,
                customMenuItemSection: found.customMenuItemSection,
                quantity: ci.quantity ?? 1,
              };
            }
            return {
              id: lookupId,
              displayName: ci.displayName,
              price: ci.unitPrice ?? 0,
              itemType: ci.isAddOn ? "GROUP_EXTRA_ITEM" : "SECTION_ITEM",
              isAddOn: ci.isAddOn || false,
              customMenuItemSection: ci.customMenuItemSection || "",
              quantity: ci.quantity ?? 1,
            };
          });

          setCustomizedCustomItem({
            ...customizedCustomItem,
            collectedItems: restoredCollectedItems,
          });
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderItems, showModal]);
  const findMenuItemByDimensions = (
    menuItems: any[],
    selectedDimensions: Record<string, string>,
    customMenuItemTypeId?: string,
  ) => {
    return menuItems.find((item) => {
      if (
        customMenuItemTypeId &&
        item.customMenuItemTypeId !== customMenuItemTypeId
      )
        return false;
      const dims = item.dimensions === null ? {} : item.dimensions;
      if (!dims) return false;
      return Object.entries(selectedDimensions).every(
        ([dimensionId, dimensionValueId]) =>
          dims[dimensionId]?.dimensionValueId === dimensionValueId,
      );
    });
  };

  const isCheckboxDisabled = (
    selectItem: MenuItem,
    value: boolean,
    itemType: string,
  ) => {
    const isCheckBoxValue = !!value;
    if (isCheckBoxValue) {
      return false;
    }

    // Check inventory availability for items with IDs
    if (selectItem.id) {
      const currentCount =
        customizedCustomItem?.collectedItems.filter(
          (item) => item.id === selectItem.id && item.itemType === itemType,
        ).length || 0;
      const inventoryCheck = checkInventoryAvailability(
        selectItem.id,
        currentCount + 1,
      );
      if (!inventoryCheck.available) {
        return true; // Disable if insufficient inventory
      }
    }
    if (itemType === "SECTION_ITEM") {
      if (
        customizedCustomItem?.collectedItems !== undefined &&
        customizedCustomItem?.collectedItems.length > 0
      ) {
        const sectionRelatedColectedItems =
          customizedCustomItem?.collectedItems.filter((item) => {
            return (
              item.customMenuItemSection ===
                selectItem?.customMenuItemSection &&
              item.itemType === "SECTION_ITEM"
            );
          });

        const section = customizedCustomItem?.sections.find(
          (sectin: any) =>
            sectin.customItemType === selectItem.customMenuItemSection,
        );

        if (sectionRelatedColectedItems?.length >= section?.maxAllowedCount) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } else if (itemType === "GROUP_EXTRA_ITEM") {
      return false;
    } else if (itemType === "SIGNAL_EXTRA_ITEM") {
      return false;
    } else {
      return true;
    }
  };
  useEffect(() => {
    if (showModal && persistedCollectedItems.length > 0) {
      setCustomizedCustomItem((prevState: any) => ({
        ...prevState,
        collectedItems: persistedCollectedItems,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, persistedCollectedItems]);

  useEffect(() => {
    if (!showModal || !customizedCustomItem?.collectedItems?.length) return;
    if (!singleMenuItemsList?.length) return;

    // Rebuild selectedDimensionValues from existing SECTION_ITEM collectedItems
    const rebuilt: {
      [sectionType: string]: { [dimensionId: string]: string };
    } = {};

    customizedCustomItem.collectedItems.forEach((item: any) => {
      if (item.itemType !== "SECTION_ITEM") return;
      const sectionType = item.customMenuItemSection;
      if (!sectionType) return;

      const dims = sectionDimensions[sectionType];
      if (!dims || dims.length === 0) return;

      // Find this item in singleMenuItemsList to get its dimension mappings
      const rawItem = singleMenuItemsList.find((m: any) => m.id === item.id);
      if (!rawItem || !rawItem.dimensions) return;

      if (!rebuilt[sectionType]) rebuilt[sectionType] = {};

      dims.forEach((dim: any) => {
        const mapping = rawItem.dimensions![dim.id];
        const dimensionValueId = Array.isArray(mapping)
          ? mapping[0]?.dimensionValueId
          : typeof mapping === "object"
            ? mapping.dimensionValueId
            : mapping;
        if (dimensionValueId) {
          rebuilt[sectionType][dim.id] = dimensionValueId;
        }
      });
    });

    if (Object.keys(rebuilt).length > 0) {
      setSelectedDimensionValues(rebuilt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, sectionDimensions]);

  // Helper: return items that belong to a specific dimension value
  const getItemsForDimensionValue = (
    section: CustomItemSection,
    dimension: any,
    val: any,
  ) => {
    if (!singleMenuItemsList) return [];

    return singleMenuItemsList.filter((item: any) => {
      if (!item.isCustomMenuItem) return false;
      if (item.customMenuItemSection !== section.customItemType) return false;

      const dims = item.dimensions === null ? {} : item.dimensions;
      if (!dims) return false;

      const mapping = dims[dimension.id];
      if (!mapping) return false;

      if (Array.isArray(mapping)) {
        return mapping.some((m: any) => m.dimensionValueId === val.id);
      }

      if (typeof mapping === "object" && mapping.dimensionValueId) {
        return mapping.dimensionValueId === val.id;
      }

      if (typeof mapping === "string") {
        return mapping === val.id;
      }

      return false;
    });
  };

  // Helper function to check if dimension value checkbox should be disabled
  const isDimensionValueDisabled = (
    section: CustomItemSection,
    dimension: any,
    val: any,
  ) => {
    return false;
  };

  // For dimension value checkboxes, checked if selectedDimensionValues has this value for this dimension
  const isDimensionValueChecked = (
    section: CustomItemSection,
    dimension: any,
    val: any,
  ) => {
    const sectionType = section.customItemType;
    const dimensionId = dimension.id;
    return selectedDimensionValues[sectionType]?.[dimensionId] === val.id;
  };

  const isItemChecked: any = (item: MenuItem, itemType: string) => {
    return customizedCustomItem?.collectedItems.some(
      (collectedItem) =>
        collectedItem.id === item.id && collectedItem.itemType === itemType,
    );
  };

  const handleSave = () => {
    if (!customizedCustomItem) {
      return;
    }

    // Separate extras and section items
    const extraItemList = customizedCustomItem.collectedItems.filter(
      (item) =>
        item.itemType === "GROUP_EXTRA_ITEM" ||
        item.itemType === "SIGNAL_EXTRA_ITEM",
    );

    // For each SECTION_ITEM, resolve the correct menu item by selected dimensions
    const resolvedSectionItems = customizedCustomItem.collectedItems
      .filter((item) => item.itemType === "SECTION_ITEM")
      .map((item) => {
        const section = customizedCustomItem.sections?.find(
          (s: any) => s.customItemType === item.customMenuItemSection,
        );
        if (!section) return item;

        const sectionType = section.customItemType;
        const sectionDims = sectionDimensions[sectionType] || [];

        // Check if this section has dimensions
        if (sectionDims.length > 0) {
          // Section WITH dimensions - use selectedDimensionValues state
          const selectedDimensions: Record<any, any> =
            selectedDimensionValues[sectionType] || {};

          // Only proceed with resolution if we have actual dimension selections
          if (Object.keys(selectedDimensions).length === 0) {
            // No dimensions selected for this section, skip resolution
            return null; // This will be filtered out later
          }

          const resolved = findMenuItemByDimensions(
            singleMenuItemsList || [],
            selectedDimensions,
            section.customMenuItemTypeId,
          );

          if (resolved) {
            return {
              ...resolved,
              itemType: "SECTION_ITEM",
              customMenuItemSection: section.customItemType,
              isAddOn: false,
            };
          }
          return null; // No matching item found, will be filtered out
        } else {
          return item; // Keep the existing item as-is
        }
      })
      .filter((item) => item !== null); // Remove null items (sections with no selection or no match)

    // Merge resolved section items and extras
    const allCollectedItems = [...resolvedSectionItems, ...extraItemList];

    console.log(
      "🔨 All collected items before processing:",
      allCollectedItems.length,
    );
    console.log(
      "🔨 All collected items:",
      allCollectedItems.map((item) => ({
        id: item.id,
        displayName: item.displayName,
        itemType: item.itemType,
        isAddOn: item.isAddOn,
      })),
    );

    // Key now includes both itemId and isAddOn to keep section items and extra items separate
    const itemsMap = new Map();

    allCollectedItems.forEach((item) => {
      const key = `${item.id}-${item.isAddOn || false}`; // Use both item ID and isAddOn as key

      if (itemsMap.has(key)) {
        // If item already exists, increment quantity
        const existingItem = itemsMap.get(key);
        existingItem.quantity = (existingItem.quantity || 1) + 1;
      } else {
        // New item, set initial quantity to 1
        itemsMap.set(key, {
          itemId: item.id,
          displayName: item.displayName,
          unitPrice: item.price,
          productId: item.productId,
          quantity: 1,
          tinyId: item.tinyId,
          skuId: item.skuId || "",
          isAddOn: item.isAddOn || false,
        });
      }
    });

    // Convert map back to array
    const savedItemsLog = Array.from(itemsMap.values());

    const customVariantKey = createCustomVariantKey(savedItemsLog);

    // Calculate and log the total for this custom item
    const basePrice =
      selectedCustomItem?.calculatedPrice || selectedCustomItem?.price || 0;
    const extrasTotal = extraItemList.reduce(
      (sum, item) => sum + (item.price || 0),
      0,
    );
    const customItemTotal = basePrice + extrasTotal;

    // Only update persistedCollectedItems
    setPersistedCollectedItems(allCollectedItems as MenuItem[]);

    // Update the parent order item with the selected custom items
    if (setOrderItems && selectedCustomItem) {
      setOrderItems((prevOrderItems: any[]) => {
        const existingItemIndex = prevOrderItems.findIndex(
          (item: any) => item.id === selectedCustomItem.id,
        );

        let updatedItems;
        if (existingItemIndex >= 0) {
          updatedItems = prevOrderItems.map((item: any, index: number) => {
            if (index === existingItemIndex) {
              console.log(
                "✏️ Updating item:",
                item.displayName || selectedCustomItem.displayName,
              );
              return {
                ...item,
                customItems: savedItemsLog,
                customItemTotal: customItemTotal,
                customVariantKey,
              };
            }
            return item;
          });
        } else {
          const defaultPortion =
            selectedCustomItem.portions && selectedCustomItem.portions[0];
          const portionLabel = defaultPortion?.displayName || "";
          const additionalCost = defaultPortion?.additionalCost || 0;

          const newOrderItem = {
            ...selectedCustomItem,
            quantity: 1,
            calculatedPrice: (selectedCustomItem.price || 0) + additionalCost,
            portionLabel,
            customItems: savedItemsLog,
            customItemTotal: customItemTotal,
            customVariantKey,
          };
          updatedItems = [...prevOrderItems, newOrderItem];
        }

        return updatedItems;
      });
    }

    handleModalClose();
  };
  // Handler for extra items, single items, and items without dimensions
  const handleSimpleItemCheckboxChange = (
    item: MenuItem,
    isChecked: boolean,
    itemType: string,
    section?: CustomItemSection,
  ) => {
    console.log("═════════════════════════════════════════════");
    console.log("🔄 handleSimpleItemCheckboxChange called");
    console.log("📦 Item:", {
      id: item.id,
      displayName: item.displayName,
      itemType: item.itemType,
    });
    console.log("✅ isChecked:", isChecked);
    console.log("🏷️ itemType (param):", itemType);
    console.log("📂 Section:", section?.displayName || "No section");

    if (!customizedCustomItem) {
      console.warn("⚠️ No customizedCustomItem found");
      return;
    }

    console.log(
      "📊 Current collected items count:",
      customizedCustomItem.collectedItems.length,
    );
    console.log(
      "📊 Current collected items:",
      customizedCustomItem.collectedItems.map((i: any) => ({
        id: i.id,
        displayName: i.displayName,
        itemType: i.itemType,
      })),
    );

    let updatedCollectedItems = [...customizedCustomItem.collectedItems];

    if (
      itemType === "SECTION_ITEM" &&
      section &&
      (!sectionDimensions[section.customItemType] ||
        sectionDimensions[section.customItemType].length === 0)
    ) {
      console.log("📍 Processing SECTION_ITEM without dimensions");
      const maxCount = section.maxAllowedCount || 1;
      console.log("📏 Max allowed count:", maxCount);

      if (maxCount === 1) {
        console.log("🎯 Radio behavior (maxCount = 1)");
        // If the item is already selected, uncheck it (toggle)
        const isAlreadySelected = updatedCollectedItems.some(
          (i: any) =>
            i.id === item.id &&
            i.itemType === "SECTION_ITEM" &&
            i.customMenuItemSection === section.customItemType,
        );

        console.log("🔍 Is already selected:", isAlreadySelected);

        if (isAlreadySelected) {
          console.log("❌ Removing item (uncheck)");
          // Uncheck: just remove it
          updatedCollectedItems = updatedCollectedItems.filter(
            (i: any) =>
              !(
                i.itemType === "SECTION_ITEM" &&
                i.customMenuItemSection === section.customItemType &&
                i.id === item.id
              ),
          );
        } else {
          console.log("✅ Adding item (check) - replacing previous selection");
          // Check: remove previous and add this one
          updatedCollectedItems = updatedCollectedItems.filter(
            (i: any) =>
              !(
                i.itemType === "SECTION_ITEM" &&
                i.customMenuItemSection === section.customItemType
              ),
          );
          updatedCollectedItems.push({
            ...item,
            itemType: "SECTION_ITEM",
            customMenuItemSection: section.customItemType,
            isAddOn: false,
          } as any);
        }
      } else {
        console.log("📋 Checkbox behavior (maxCount > 1)");
        // Checkbox-like: only remove this item if unchecked, add if checked and under limit
        const sectionItems = updatedCollectedItems.filter(
          (i: any) =>
            i.itemType === "SECTION_ITEM" &&
            i.customMenuItemSection === section.customItemType,
        );

        console.log(
          `📊 Current section items count: ${sectionItems.length}/${maxCount}`,
        );

        if (isChecked) {
          if (sectionItems.length < maxCount) {
            console.log("✅ Adding item (under limit)");
            updatedCollectedItems = [
              ...updatedCollectedItems,
              {
                ...item,
                itemType: "SECTION_ITEM",
                customMenuItemSection: section.customItemType,
                isAddOn: false,
              } as any,
            ];
          } else {
            console.warn("⚠️ Cannot add - reached max limit");
          }
        } else {
          updatedCollectedItems = updatedCollectedItems.filter(
            (i: any) =>
              !(
                i.itemType === "SECTION_ITEM" &&
                i.customMenuItemSection === section.customItemType &&
                i.id === item.id
              ),
          );
        }
      }
    } else {
      if (isChecked) {
        // Add one instance of the item
        const isExtraItem =
          itemType === "GROUP_EXTRA_ITEM" || itemType === "SIGNAL_EXTRA_ITEM";
        updatedCollectedItems.push({
          ...item,
          itemType,
          isAddOn: isExtraItem,
        } as any);
      } else {
        // Remove one instance of the item (find and remove the first occurrence)
        const itemIndex = updatedCollectedItems.findIndex(
          (i: any) => i.id === item.id && i.itemType === itemType,
        );
        if (itemIndex !== -1) {
          updatedCollectedItems.splice(itemIndex, 1);
        } else {
        }
      }
    }
    setCustomizedCustomItem({
      ...customizedCustomItem,
      collectedItems: updatedCollectedItems,
    });
    setPersistedCollectedItems(updatedCollectedItems as MenuItem[]);
    checkRequiredSectionsSelection();
  };

  return (
    <Modal
      show={showModal}
      onHide={handleModalClose}
      backdrop="static"
      size="lg"
      className="modal-xl"
      style={{
        overflow: "hidden",
      }}
    >
      <Modal.Header closeButton>
        <div className="d-flex justify-content-between w-100">
          <div className="header-text-pop-up">
            {selectedCustomItem?.displayName}
          </div>
        </div>
      </Modal.Header>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1050,
          backgroundColor: "#28a745",
          color: "white",
          padding: "12px 20px",
          fontWeight: "bold",
          fontSize: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "2px solid #1e7e34",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <span>Total Price:</span>
        <span style={{ fontSize: "18px" }}>LKR {totalPrice.toFixed(2)}</span>
      </div>
      <Modal.Body
        style={{ maxHeight: "calc(100vh - 250px)", overflowY: "auto" }}
      >
        {selectedCustomItem?.sections.length > 0 && (
          <>
            {selectedCustomItem?.sections.map(
              (section: CustomItemSection, index: number) => (
                <div key={index}>
                  {/* Show top section label only when we don't have dimensions for this section
                      to avoid duplicating the dimension display name (e.g. "Select Fusion"). */}
                  {!(
                    sectionDimensions[section.customItemType] &&
                    sectionDimensions[section.customItemType].length > 0
                  ) && (
                    <>
                      <Label className="mt-1 fw-bold">
                        {section.displayName}
                        {section.isRequired && (
                          <span
                            className="ms-1"
                            style={{ color: "#ff0000", fontWeight: "bold" }}
                          >
                            *
                          </span>
                        )}
                      </Label>
                      &nbsp;
                    </>
                  )}
                  {/* {section.isRequired && (
                    <FontAwesomeIcon
                      icon={faStar}
                      color={"red"}
                      title={"section is required"}
                    />
                  )} */}
                  &nbsp;
                  {/* <Badge color={"warning"} style={{ opacity: 0.7 }}>
                    <FontAwesomeIcon
                      icon={faBowlFood}
                      size={"sm"}
                      color={"black"}
                    />{" "}
                    &nbsp;{section?.customItemType}
                  </Badge> */}
                  <p className="text-red">
                    Select up to {section.maxAllowedCount} items{" "}
                  </p>
                  {/* Render fetched dimensions (if any) for this section, sorted by selectionOrder and displayOrder
                      If dimensions exist we group items under each dimension value. If no dimensions, fall back to flat listing.
                      While dimensions are being fetched for a section, show a simple loading skeleton to avoid confusing the user. */}
                  {section.customMenuItemTypeId &&
                  sectionLoading[section.customItemType] ? (
                    <div className="section-dimensions">
                      {[...Array(2)].map((_, dimIdx) => (
                        <div
                          key={`skeleton-dim-${dimIdx}`}
                          className="dimension-block mb-3"
                        >
                          <Label className="fw-semibold">Loading...</Label>
                          <div
                            style={{ display: "flex", gap: 12, marginTop: 8 }}
                          >
                            {[...Array(3)].map((__, valIdx) => (
                              <div
                                key={`skeleton-val-${valIdx}`}
                                style={{
                                  flex: "1 1 30%",
                                  padding: 12,
                                  borderRadius: 8,
                                  background: "#f3f3f3",
                                }}
                              >
                                <div
                                  style={{
                                    height: 14,
                                    width: "70%",
                                    background: "#e0e0e0",
                                    borderRadius: 4,
                                    marginBottom: 8,
                                  }}
                                />
                                <div
                                  style={{
                                    height: 12,
                                    width: "50%",
                                    background: "#e0e0e0",
                                    borderRadius: 4,
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : sectionDimensions[section.customItemType] &&
                    sectionDimensions[section.customItemType].length > 0 ? (
                    <div className="section-dimensions">
                      {/* Show selection progress for this section */}
                      {(() => {
                        const dims =
                          sectionDimensions[section.customItemType] || [];
                        const currentSelections =
                          selectedDimensionValues[section.customItemType] || {};
                        const selectedCount =
                          Object.keys(currentSelections).length;
                        const totalCount = dims.length;

                        if (selectedCount > 0 && selectedCount < totalCount) {
                          return (
                            <div className="alert alert-warning small mb-2">
                              <strong>Selection Progress:</strong>{" "}
                              {selectedCount} of {totalCount} dimensions
                              selected. Please complete all dimensions or
                              uncheck your current selections.
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {sectionDimensions[section.customItemType]
                        .slice()
                        .sort(
                          (a: any, b: any) =>
                            (a.selectionOrder || 0) - (b.selectionOrder || 0),
                        )
                        .map((dimension: any) => (
                          <div
                            key={dimension.id}
                            className="dimension-block mb-3"
                          >
                            <Label className="fw-semibold">
                              {dimension.displayName}
                              {dimension.isRequired && (
                                <span
                                  className="ms-1"
                                  style={{
                                    color: "#ff0000",
                                    fontWeight: "bold",
                                  }}
                                >
                                  *
                                </span>
                              )}
                            </Label>
                            {/* For each dimension value, show a header and the matching items */}
                            {(dimension.dimensionValue || [])
                              .slice()
                              .sort(
                                (x: any, y: any) =>
                                  (x.displayOrder || 0) - (y.displayOrder || 0),
                              )
                              .map((val: any) => {
                                const itemsForVal = getItemsForDimensionValue(
                                  section,
                                  dimension,
                                  val,
                                );
                                // if (!itemsForVal || itemsForVal.length === 0) return (
                                //   <div key={val.id} className="dimension-value-item text-secondary small mb-2">
                                //     {val.displayName}
                                //     <div className="text-muted small">No items for this option</div>
                                //   </div>
                                // );
                                if (!itemsForVal || itemsForVal.length === 0)
                                  return null;

                                // For each dimension value show a single card using the
                                // dimension value's displayName and description. Use the
                                // first matching item as the backing item for price/selection.
                                const representative = itemsForVal[0];
                                // const priceLabel = representative ? `Rs.(${representative.price || 0})` : "";

                                return (
                                  <div
                                    key={val.id}
                                    className="mb-2 dimension-value-block p-2 border rounded"
                                    style={{
                                      backgroundColor: isDimensionValueChecked(
                                        section,
                                        dimension,
                                        val,
                                      )
                                        ? "#e8f5e8"
                                        : "white",
                                      borderColor: isDimensionValueChecked(
                                        section,
                                        dimension,
                                        val,
                                      )
                                        ? "#28a745"
                                        : "#dee2e6",
                                      cursor: representative
                                        ? "pointer"
                                        : "default",
                                    }}
                                    onClick={() => {
                                      if (representative) {
                                        const currentlyChecked =
                                          isDimensionValueChecked(
                                            section,
                                            dimension,
                                            val,
                                          );
                                        // Radio-like behavior: if already selected, uncheck; if not selected, check
                                        handleDimensionValueCheckboxChange(
                                          section,
                                          dimension,
                                          val,
                                          !currentlyChecked,
                                        );
                                      }
                                    }}
                                  >
                                    <div className="d-flex align-items-center justify-content-between">
                                      <div className="dimension-value-item fw-semibold mb-1">
                                        {val.displayName}
                                      </div>
                                      <div className="form-check checkbox-container">
                                        {representative ? (
                                          <Input
                                            id={`isCollectCustomItem-sectionItem-${section.customItemType}-${dimension.id}-${val.id}`}
                                            type="checkbox"
                                            checked={isDimensionValueChecked(
                                              section,
                                              dimension,
                                              val,
                                            )}
                                            onChange={(
                                              e: React.ChangeEvent<HTMLInputElement>,
                                            ) => {
                                              e.stopPropagation(); // Prevent card click when clicking checkbox directly
                                              const isChecked =
                                                e.target.checked;
                                              handleDimensionValueCheckboxChange(
                                                section,
                                                dimension,
                                                val,
                                                isChecked,
                                              );
                                            }}
                                            disabled={isDimensionValueDisabled(
                                              section,
                                              dimension,
                                              val,
                                            )}
                                          />
                                        ) : (
                                          <div className="text-muted small">
                                            No option available
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div
                                      key={val.id + "-card"}
                                      className={`custom-item-row ${representative?.isAvailable ? "disabled-row" : ""}`}
                                      style={{
                                        backgroundColor: "transparent",
                                        pointerEvents: "none", // Let parent handle clicks
                                        opacity: representative?.isAvailable
                                          ? 1
                                          : 0.6,
                                        display: "flex",
                                        gap: "12px",
                                        padding: "8px 0",
                                      }}
                                    >
                                      {/* Image section */}
                                      <div style={{ flexShrink: 0 }}>
                                        <img
                                          src={getImageUrlWithFallback(
                                            val.imageUrl,
                                            DEFAULT_IMAGE_URL,
                                            64,
                                          )}
                                          alt="dimension-value-img"
                                          style={{
                                            width: "60px",
                                            height: "60px",
                                            objectFit: "cover",
                                            borderRadius: "8px",
                                          }}
                                        />
                                      </div>

                                      {/* Content section */}
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div
                                          className="fw-semibold text-truncate"
                                          style={{
                                            fontSize: "14px",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          {val.displayName}
                                        </div>
                                        {val.description && (
                                          <div
                                            className="text-secondary small text-truncate"
                                            style={{
                                              marginBottom: "8px",
                                              fontSize: "12px",
                                            }}
                                          >
                                            {val.description}
                                          </div>
                                        )}
                                        {/* {priceLabel && (
                                          <div 
                                            className="fw-semibold" 
                                            style={{ 
                                              color: '#dc3545', 
                                              fontSize: '12px',
                                              backgroundColor: '#fff',
                                              border: '1px solid #dc3545',
                                              borderRadius: '12px',
                                              padding: '2px 8px',
                                              display: 'inline-block'
                                            }}
                                          >
                                            LKR {representative?.price || 0}.00
                                          </div>
                                        )} */}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        ))}
                      {unavailabilityWarning && (
                        <div className="alert alert-warning mt-3" role="alert">
                          <strong>Warning:</strong> {unavailabilityWarning}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      {singleMenuItemsList
                        ?.filter((singleItem) => {
                          const formValues = watch && watch();
                          const isScheduledOrder = formValues?.isScheduledOrder;
                          const orderDate = formValues?.date;
                          const effectiveDayIndex =
                            isScheduledOrder && orderDate
                              ? new Date(orderDate).getDay()
                              : new Date().getDay();
                          const availableDays = singleItem?.availableDays || [];
                          const isAvailableToday =
                            availableDays.length > 0 &&
                            availableDays.includes(effectiveDayIndex);

                          return (
                            singleItem.customMenuItemSection ===
                              section.customItemType &&
                            singleItem?.isCustomMenuItem &&
                            singleItem?.isAvailable !== false &&
                            isAvailableToday
                          );
                        })
                        .map((filteredItem, idx) => {
                          const isSelected = isItemChecked(
                            filteredItem,
                            "SECTION_ITEM",
                          );
                          return (
                            <div
                              key={idx}
                              className="mb-2 dimension-value-block p-2 border rounded"
                              style={{
                                backgroundColor: isSelected
                                  ? "#e8f5e8"
                                  : "white",
                                borderColor: isSelected ? "#28a745" : "#dee2e6",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                // Radio-like behavior: if already selected, uncheck; if not selected, check
                                handleSimpleItemCheckboxChange(
                                  filteredItem,
                                  !isSelected,
                                  "SECTION_ITEM",
                                  section,
                                );
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="dimension-value-item fw-semibold mb-1">
                                  {filteredItem.displayName}
                                </div>
                                <div className="form-check checkbox-container">
                                  <Input
                                    id={`isCollectCustomItem-sectionItem-${filteredItem.id}`}
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => {
                                      e.stopPropagation(); // Prevent card click when clicking checkbox directly
                                      const isChecked = e.target.checked;
                                      handleSimpleItemCheckboxChange(
                                        filteredItem,
                                        isChecked,
                                        "SECTION_ITEM",
                                        section,
                                      );
                                    }}
                                    disabled={isCheckboxDisabled(
                                      filteredItem,
                                      isSelected,
                                      "SECTION_ITEM",
                                    )}
                                  />
                                </div>
                              </div>
                              <div
                                className={`custom-item-row`}
                                style={{
                                  backgroundColor: "transparent",
                                  pointerEvents: "none", // Let parent handle clicks
                                  opacity: 1,
                                  display: "flex",
                                  gap: "12px",
                                  padding: "8px 0",
                                }}
                              >
                                {/* Image section */}
                                <div style={{ flexShrink: 0 }}>
                                  <img
                                    src={getImageUrlWithFallback(
                                      filteredItem.imageUrl[0],
                                      DEFAULT_IMAGE_URL,
                                      64,
                                    )}
                                    alt="menu-item"
                                    style={{
                                      width: "60px",
                                      height: "60px",
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                    }}
                                  />
                                </div>

                                {/* Content section */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  {/* <div className="fw-semibold text-truncate" style={{ fontSize: '14px', marginBottom: '4px' }}>
                                    {filteredItem.displayName}
                                  </div> */}
                                  {filteredItem?.description && (
                                    <div
                                      className="text-secondary small text-truncate"
                                      style={{
                                        marginBottom: "8px",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {filteredItem.description}
                                    </div>
                                  )}
                                  {/* <div 
                                    className="fw-semibold" 
                                    style={{ 
                                      color: '#dc3545', 
                                      fontSize: '12px',
                                      backgroundColor: '#fff',
                                      border: '1px solid #dc3545',
                                      borderRadius: '12px',
                                      padding: '2px 8px',
                                      display: 'inline-block'
                                    }}
                                  >
                                    LKR {filteredItem.price || 0}.00
                                  </div> */}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              ),
            )}
          </>
        )}
        {/*--- Extra Items ---*/}
        <div>
          {/* <hr style={{ margin: "5px 0" }} /> */}
          <Label className="mt-3 fw-bold">_ _ _Extra Items_ _ _</Label>
          {selectedCustomItem?.extraItems ? (
            selectedCustomItem.extraItems.map(
              (extraItem: extraItem, index: number) => (
                <div key={index}>
                  {extraItem?.type === "group" ? (
                    <div>
                      {/*Group Extra Items */}
                      {/* <div>{extraItem.displayName} </div> */}

                      {singleMenuItemsList
                        ?.filter((singleItem) => {
                          const formValues = watch && watch();
                          const isScheduledOrder = formValues?.isScheduledOrder;
                          const orderDate = formValues?.date;
                          const effectiveDayIndex =
                            isScheduledOrder && orderDate
                              ? new Date(orderDate).getDay()
                              : new Date().getDay();
                          const availableDays = singleItem?.availableDays || [];
                          const isAvailableToday =
                            availableDays.length > 0 &&
                            availableDays.includes(effectiveDayIndex);

                          return (
                            singleItem.customMenuItemSection ===
                              extraItem?.customItemType &&
                            singleItem?.isCustomMenuItem &&
                            singleItem?.isAvailable !== false &&
                            isAvailableToday
                          );
                        })
                        .map((filteredItem, idx) => {
                          const currentQuantity =
                            customizedCustomItem?.collectedItems.filter(
                              (item) =>
                                item.id === filteredItem.id &&
                                item.itemType === "GROUP_EXTRA_ITEM",
                            ).length || 0;
                          const isSelected = currentQuantity > 0;
                          return (
                            <div key={idx}>
                              <div
                                className="mb-2 dimension-value-block p-2 border rounded"
                                style={{
                                  backgroundColor: isSelected
                                    ? "#e8f5e8"
                                    : "white",
                                  borderColor: isSelected
                                    ? "#28a745"
                                    : "#dee2e6",
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  // Container click now does nothing, quantity is controlled by buttons
                                }}
                              >
                                <div className="d-flex align-items-center justify-content-between">
                                  <div className="dimension-value-item fw-semibold mb-1">
                                    {filteredItem.displayName}
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    {(() => {
                                      const currentQuantity =
                                        customizedCustomItem?.collectedItems.filter(
                                          (item) =>
                                            item.id === filteredItem.id &&
                                            item.itemType ===
                                              "GROUP_EXTRA_ITEM",
                                        ).length || 0;

                                      return (
                                        <>
                                          <button
                                            className="btn btn-sm btn-outline-secondary"
                                            style={{
                                              width: "30px",
                                              height: "30px",
                                              padding: "0",
                                              fontSize: "14px",
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (currentQuantity > 0) {
                                                handleSimpleItemCheckboxChange(
                                                  filteredItem,
                                                  false,
                                                  "GROUP_EXTRA_ITEM",
                                                );
                                              }
                                            }}
                                            disabled={currentQuantity === 0}
                                          >
                                            -
                                          </button>
                                          <input
                                            type="number"
                                            min="0"
                                            value={currentQuantity}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              const newQuantity =
                                                parseInt(e.target.value) || 0;

                                              if (!customizedCustomItem) return;

                                              // Remove all existing instances of this item
                                              let updatedCollectedItems =
                                                customizedCustomItem.collectedItems.filter(
                                                  (item: any) =>
                                                    !(
                                                      item.id ===
                                                        filteredItem.id &&
                                                      item.itemType ===
                                                        "GROUP_EXTRA_ITEM"
                                                    ),
                                                );

                                              // Add the new quantity amount
                                              for (
                                                let i = 0;
                                                i < newQuantity;
                                                i++
                                              ) {
                                                updatedCollectedItems.push({
                                                  ...filteredItem,
                                                  itemType: "GROUP_EXTRA_ITEM",
                                                } as any);
                                              }

                                              setCustomizedCustomItem({
                                                ...customizedCustomItem,
                                                collectedItems:
                                                  updatedCollectedItems,
                                              });
                                              setPersistedCollectedItems(
                                                updatedCollectedItems as MenuItem[],
                                              );
                                              checkRequiredSectionsSelection();
                                            }}
                                            style={{
                                              width: "50px",
                                              height: "30px",
                                              textAlign: "center",
                                              fontSize: "14px",
                                              fontWeight: "bold",
                                              border: "1px solid #dee2e6",
                                              borderRadius: "4px",
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <button
                                            className="btn btn-sm btn-outline-secondary"
                                            style={{
                                              width: "30px",
                                              height: "30px",
                                              padding: "0",
                                              fontSize: "14px",
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleSimpleItemCheckboxChange(
                                                filteredItem,
                                                true,
                                                "GROUP_EXTRA_ITEM",
                                              );
                                            }}
                                            disabled={isCheckboxDisabled(
                                              filteredItem,
                                              false,
                                              "GROUP_EXTRA_ITEM",
                                            )}
                                          >
                                            +
                                          </button>
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                                <div
                                  className={`custom-item-row`}
                                  style={{
                                    backgroundColor: "transparent",
                                    pointerEvents: "none",
                                    opacity: 1,
                                    display: "flex",
                                    gap: "12px",
                                    padding: "8px 0",
                                  }}
                                >
                                  {/* Image section */}
                                  <div style={{ flexShrink: 0 }}>
                                    <img
                                      src={filteredItem.imageUrl}
                                      alt="group-extra-item"
                                      style={{
                                        width: "60px",
                                        height: "60px",
                                        objectFit: "cover",
                                        borderRadius: "8px",
                                      }}
                                    />
                                  </div>

                                  {/* Content section */}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                      className="fw-semibold text-truncate"
                                      style={{
                                        fontSize: "14px",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      {filteredItem.displayName}
                                    </div>
                                    {filteredItem?.description && (
                                      <div
                                        className="text-secondary small text-truncate"
                                        style={{
                                          marginBottom: "8px",
                                          fontSize: "12px",
                                        }}
                                      >
                                        {filteredItem.description}
                                      </div>
                                    )}
                                    <div
                                      className="fw-semibold"
                                      style={{
                                        color: "#dc3545",
                                        fontSize: "12px",
                                        backgroundColor: "#fff",
                                        border: "1px solid #dc3545",
                                        borderRadius: "12px",
                                        padding: "2px 8px",
                                        display: "inline-block",
                                      }}
                                    >
                                      LKR {filteredItem?.price || 0}.00
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div>
                      {/*Single Extra items ===========*/}
                      {/* <Label className="mt-2 fw-bold">Single Items</Label> */}
                      <div>
                        {singleMenuItemsList
                          ?.filter((singleItem) => {
                            const formValues = watch && watch();
                            const isScheduledOrder =
                              formValues?.isScheduledOrder;
                            const orderDate = formValues?.date;
                            const effectiveDayIndex =
                              isScheduledOrder && orderDate
                                ? new Date(orderDate).getDay()
                                : new Date().getDay();
                            const availableDays =
                              singleItem?.availableDays || [];
                            const isAvailableToday =
                              availableDays.length > 0 &&
                              availableDays.includes(effectiveDayIndex);

                            return (
                              singleItem?.id === extraItem?.menuItemId &&
                              singleItem?.isCustomMenuItem &&
                              singleItem?.isAvailable !== false &&
                              isAvailableToday
                            );
                          })
                          .map((filteredItem, idx) => {
                            const currentQuantity =
                              customizedCustomItem?.collectedItems.filter(
                                (item) =>
                                  item.id === filteredItem.id &&
                                  item.itemType === "SIGNAL_EXTRA_ITEM",
                              ).length || 0;
                            const isSelected = currentQuantity > 0;
                            return (
                              <div key={idx}>
                                <div
                                  className="mb-2 dimension-value-block p-2 border rounded"
                                  style={{
                                    backgroundColor: isSelected
                                      ? "#e8f5e8"
                                      : "white",
                                    borderColor: isSelected
                                      ? "#28a745"
                                      : "#dee2e6",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    // Container click now does nothing, quantity is controlled by buttons
                                  }}
                                >
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div className="dimension-value-item fw-semibold mb-1">
                                      {filteredItem.displayName}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      {(() => {
                                        const currentQuantity =
                                          customizedCustomItem?.collectedItems.filter(
                                            (item) =>
                                              item.id === filteredItem.id &&
                                              item.itemType ===
                                                "SIGNAL_EXTRA_ITEM",
                                          ).length || 0;

                                        return (
                                          <>
                                            <button
                                              className="btn btn-sm btn-outline-secondary"
                                              style={{
                                                width: "30px",
                                                height: "30px",
                                                padding: "0",
                                                fontSize: "14px",
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (currentQuantity > 0) {
                                                  handleSimpleItemCheckboxChange(
                                                    filteredItem,
                                                    false,
                                                    "SIGNAL_EXTRA_ITEM",
                                                  );
                                                }
                                              }}
                                              disabled={currentQuantity === 0}
                                            >
                                              -
                                            </button>
                                            <input
                                              type="number"
                                              min="0"
                                              value={currentQuantity}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                const newQuantity =
                                                  parseInt(e.target.value) || 0;

                                                if (!customizedCustomItem)
                                                  return;

                                                // Remove all existing instances of this item
                                                let updatedCollectedItems =
                                                  customizedCustomItem.collectedItems.filter(
                                                    (item: any) =>
                                                      !(
                                                        item.id ===
                                                          filteredItem.id &&
                                                        item.itemType ===
                                                          "SIGNAL_EXTRA_ITEM"
                                                      ),
                                                  );

                                                // Add the new quantity amount
                                                for (
                                                  let i = 0;
                                                  i < newQuantity;
                                                  i++
                                                ) {
                                                  updatedCollectedItems.push({
                                                    ...filteredItem,
                                                    itemType:
                                                      "SIGNAL_EXTRA_ITEM",
                                                  } as any);
                                                }

                                                setCustomizedCustomItem({
                                                  ...customizedCustomItem,
                                                  collectedItems:
                                                    updatedCollectedItems,
                                                });
                                                setPersistedCollectedItems(
                                                  updatedCollectedItems as MenuItem[],
                                                );
                                                checkRequiredSectionsSelection();
                                              }}
                                              style={{
                                                width: "50px",
                                                height: "30px",
                                                textAlign: "center",
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                                border: "1px solid #dee2e6",
                                                borderRadius: "4px",
                                              }}
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                            />
                                            <button
                                              className="btn btn-sm btn-outline-secondary"
                                              style={{
                                                width: "30px",
                                                height: "30px",
                                                padding: "0",
                                                fontSize: "14px",
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleSimpleItemCheckboxChange(
                                                  filteredItem,
                                                  true,
                                                  "SIGNAL_EXTRA_ITEM",
                                                );
                                              }}
                                              disabled={isCheckboxDisabled(
                                                filteredItem,
                                                false,
                                                "SIGNAL_EXTRA_ITEM",
                                              )}
                                            >
                                              +
                                            </button>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                  <div
                                    className={`custom-item-row`}
                                    style={{
                                      backgroundColor: "transparent",
                                      pointerEvents: "none",
                                      opacity: 1,
                                      display: "flex",
                                      gap: "12px",
                                      padding: "8px 0",
                                    }}
                                  >
                                    {/* Image section */}
                                    <div style={{ flexShrink: 0 }}>
                                      <img
                                        src={getImageUrlWithFallback(
                                          filteredItem.imageUrl[0],
                                          DEFAULT_IMAGE_URL,
                                          64,
                                        )}
                                        // src={filteredItem.imageUrl}
                                        alt="signal-extra-item"
                                        style={{
                                          width: "60px",
                                          height: "60px",
                                          objectFit: "cover",
                                          borderRadius: "8px",
                                        }}
                                      />
                                    </div>

                                    {/* Content section */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div
                                        className="fw-semibold text-truncate"
                                        style={{
                                          fontSize: "14px",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        {filteredItem.displayName}
                                      </div>
                                      {filteredItem?.description && (
                                        <div
                                          className="text-secondary small text-truncate"
                                          style={{
                                            marginBottom: "8px",
                                            fontSize: "12px",
                                          }}
                                        >
                                          {filteredItem.description}
                                        </div>
                                      )}
                                      <div
                                        className="fw-semibold"
                                        style={{
                                          color: "#dc3545",
                                          fontSize: "12px",
                                          backgroundColor: "#fff",
                                          border: "1px solid #dc3545",
                                          borderRadius: "12px",
                                          padding: "2px 8px",
                                          display: "inline-block",
                                        }}
                                      >
                                        LKR {filteredItem.price || 0}.00
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              ),
            )
          ) : (
            <div>No extra items available</div>
          )}
        </div>
        <div className="d-flex justify-content-end">
          <ButtonComponent
            onClick={() => handleSave()}
            isDisabled={!isSaveButtonEnabled}
            titel="Save"
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};
export default CustomItemSetupModal;
